import { ReactElement, useEffect } from 'react';
import { Box, Checkbox, Flex, Text } from '@chakra-ui/react';
import {
  useForm,
  SubmitHandler,
  FieldErrors,
  FieldError,
  Controller,
} from 'react-hook-form';
import { AiOutlineArrowRight, AiOutlineMail } from 'react-icons/ai';
import { yupResolver } from '@hookform/resolvers/yup';

import Button from 'components/atoms/button';
import Field from 'components/molecules/form/field';
import {
  firstStepRegistrationSchema,
  InputsFirstStep,
} from 'lib/schemas/candidates/firstStepRegistration';
import { POLITICS, TERMS } from 'lib/constants/paths';

export interface FormFirstStepProps {
  submitFirstStep: SubmitHandler<InputsFirstStep>;
  formErrors: FieldErrors;
}

const FormFirstStep = ({
  submitFirstStep,
  formErrors,
}: FormFirstStepProps): ReactElement => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    control,
  } = useForm<InputsFirstStep>({
    resolver: yupResolver(firstStepRegistrationSchema),
  });

  useEffect(() => {
    formErrors?.forEach((formError: FieldError) =>
      setError('email', formError)
    );
  }, [formErrors, setError]);

  return (
    <>
      <Box as='form' onSubmit={handleSubmit(submitFirstStep)}>
        <Flex flexWrap='wrap'>
          <Field
            height='50px'
            width={['100%', '100%', '300px', '300px']}
            mr={[0, 0, 4, 4]}
            mb={[4, 4, 0, 0]}
            leftIcon={AiOutlineMail}
            aria-label='E-mail'
            label='E-mail'
            placeholder='Digite o seu e-mail'
            {...register('email', { required: true })}
            errors={errors}
            isRequired
          />

          <Button
            size='xl'
            mt='37px'
            w={['100%', '100%', '240px', '240px']}
            rightIcon={AiOutlineArrowRight}
            type='submit'
            aria-label='Criar meu perfil agora'
          >
            Criar meu perfil agora
          </Button>
        </Flex>
        <Flex
          fontSize='0.9rem'
          color='text.200'
          flexDirection={{ base: 'column', sm: 'row' }}
          pt='4'
          pb='2'
        >
          <Text mr={1}>Ao se cadastrar, você aceita nossos</Text>
          <a href={TERMS} target='_blank' rel='noreferrer'>
            <Text cursor='pointer' textDecor='underline'>
              termos de uso
            </Text>
          </a>
          <Text mr={1} ml={1}>
            e nossa
          </Text>
          <a href={POLITICS} target='_blank' rel='noreferrer'>
            <Text cursor='pointer' textDecor='underline'>
              política de privacidade
            </Text>
          </a>
        </Flex>

        <Controller
          control={control}
          name='acceptEmailNotifications'
          defaultValue={false}
          render={({ field: { onChange, value, ref } }) => (
            <Checkbox
              aria-label='Aceito receber e-mails com dicas da Geek'
              borderColor='text.100'
              color='text.100'
              colorScheme='secondary'
              fontSize='14px'
              isChecked={value}
              onChange={onChange}
              ref={ref}
              css={`
                > span:first-of-type {
                  box-shadow: unset;
                }
              `}
            >
              Aceito receber e-mails com dicas da Geek
            </Checkbox>
          )}
        />
      </Box>
    </>
  );
};

export default FormFirstStep;
